import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { trpcClient } from '@/lib/trpc'
import { LANGUAGE_ENUM_VALUES } from '@repo/sdk'
import { Loader2, Mail, Send } from 'lucide-react'
import { useState } from 'react'

type BulkLanguage = 'all' | (typeof LANGUAGE_ENUM_VALUES)[number]

const LANGUAGE_LABELS: Record<(typeof LANGUAGE_ENUM_VALUES)[number], string> = {
  zh: '🇨🇳 简体中文',
  tw: '🇹🇼 繁體中文',
  en: '🇬🇧 English',
  fr: '🇫🇷 Français',
  es: '🇪🇸 Español',
  ja: '🇯🇵 日本語',
  ko: '🇰🇷 한국어',
  ar: '🇸🇦 العربية',
}

type ResultState = {
  success: boolean
  message: string
  details?: { accepted: number, failed: number, failedEmails?: string[], batchId?: string }
}

export function EmailServiceSection() {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [recipientList, setRecipientList] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ResultState | null>(null)
  const [mode, setMode] = useState<'list' | 'bulk'>('list')
  const [showBulkConfirm, setShowBulkConfirm] = useState(false)
  const [bulkLanguage, setBulkLanguage] = useState<BulkLanguage>('all')
  const [paidOnly, setPaidOnly] = useState(false)
  const { t } = useI18n()

  const handleSend = async (targetMode: 'list' | 'bulk') => {
    const specificEmails = targetMode === 'list'
      ? recipientList.split(/[\n,]/).map(e => e.trim()).filter(Boolean)
      : undefined

    if (!subject || !body) {
      setResult({ success: false, message: 'Subject and Body are required' })
      return
    }

    if (targetMode === 'list' && specificEmails?.length === 0) {
      setResult({ success: false, message: 'Recipient list is required' })
      return
    }

    if (targetMode === 'bulk' && !showBulkConfirm) {
      setMode('bulk')
      setShowBulkConfirm(true)
      return
    }

    setShowBulkConfirm(false)

    setLoading(true)
    setResult(null)
    setMode(targetMode)

    try {
      const languageRefresh = await trpcClient.batchRouter.updateUserLanguages.mutate()
      if (!languageRefresh.success) {
        throw new Error('User language refresh failed; email was not sent')
      }

      const response = await trpcClient.batchRouter.sendMarketingEmailBatch.mutate({
        subject,
        htmlBody: body,
        specificEmails,
        targetLanguage: targetMode === 'bulk' && bulkLanguage !== 'all'
          ? bulkLanguage
          : undefined,
        paidOnly: targetMode === 'bulk' ? paidOnly : undefined,
      })

      setResult({
        success: response.success
          && (response.details === undefined
            || (response.details.accepted > 0 && response.details.failed === 0)),
        message: response.message,
        details: response.details,
      })
    }
    catch (error: unknown) {
      const message
        = error instanceof Error ? error.message : 'Failed to send email'
      setResult({ success: false, message })
    }
    finally {
      setLoading(false)
    }
  }

  const bulkLangLabel = bulkLanguage === 'all' ? 'All' : LANGUAGE_LABELS[bulkLanguage]
  const bulkAudienceLabel = `${bulkLangLabel}${paidOnly ? ' Paid' : ''} Users`

  return (
    <div className="container mx-auto max-w-4xl space-y-6 py-8">
      { }
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            Email Service
          </CardTitle>
          <CardDescription>
            Send marketing emails to users. Use simple Markdown or HTML.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Enter email subject"
            />
          </div>

          <div className="space-y-2">
            <Label>Email Body (HTML supported)</Label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="<h1>Hello!</h1><p>This is a marketing email.</p>"
              className="min-h-[200px] font-mono"
            />
          </div>

          <div className="space-y-4 border-t pt-4">
            { }
            <div className="space-y-2">
              <Label>Recipients (comma or new line separated)</Label>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <Textarea
                  value={recipientList}
                  onChange={e => setRecipientList(e.target.value)}
                  placeholder="alice@example.com, bob@example.com"
                  className="min-h-[80px] flex-1 font-mono"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleSend('list')}
                  disabled={loading}
                  className="mb-1"
                >
                  {loading && mode === 'list' ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 size-4" />
                  )}
                  <span className="ml-2">Send to List</span>
                </Button>
              </div>
            </div>

            { }
            <div className="space-y-3 border-t pt-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-3">
                  <Label className="whitespace-nowrap">Target Language:</Label>
                  <select
                    value={bulkLanguage}
                    onChange={e => setBulkLanguage(e.target.value as BulkLanguage)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="all">All Users</option>
                    {LANGUAGE_ENUM_VALUES.map(language => (
                      <option key={language} value={language}>
                        {LANGUAGE_LABELS[language]}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={paidOnly}
                    onChange={e => setPaidOnly(e.target.checked)}
                    className="size-4"
                  />
                  💳 Paid users only
                </label>
              </div>
              {paidOnly && (
                <p className="text-xs text-muted-foreground">
                  Only users with a real-money purchase (Stripe or Steam). Comped / free-credit accounts are excluded.
                </p>
              )}
              <Button
                type="button"
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={() => {
                  handleSend('bulk')
                }}
                disabled={loading}
              >
                {loading && mode === 'bulk' ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 size-4" />
                )}
                <span className="ml-2">{`Send to ${bulkAudienceLabel}`}</span>
              </Button>
            </div>
          </div>

          {showBulkConfirm && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulk-email-confirm-title"
            >
              <div className="mx-4 w-full max-w-md rounded-lg border bg-background p-6">
                <h3 id="bulk-email-confirm-title" className="mb-4 text-lg font-semibold">
                  {`Send to ${bulkAudienceLabel}?`}
                </h3>
                <p className="mb-6 text-muted-foreground">
                  {`This will email ${bulkLanguage === 'all' ? 'all eligible users' : `eligible ${LANGUAGE_LABELS[bulkLanguage]} users`}${paidOnly ? ' who have made a real-money purchase (Stripe or Steam)' : ''}. This action cannot be undone.`}
                </p>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBulkConfirm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => handleSend('bulk')}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      'Confirm Send'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {result && (
            <div className={`rounded-lg p-4 ${result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              <p className="font-semibold">{result.message}</p>
              {result.details !== undefined ? (
                <div className="mt-2 text-sm">
                  <span>Accepted by provider: {result.details.accepted}, Rejected: {result.details.failed}</span>
                  {result.details.batchId && (
                    <p className="mt-1 font-mono text-xs">Batch ID: {result.details.batchId}</p>
                  )}
                  {
                  }
                  <p className="mt-1 text-xs opacity-80">
                    Acceptance is not delivery. Bounces and spam reports arrive over the next
                    few minutes — see the delivery report below for what actually landed.
                  </p>
                </div>
              ) : null}
              {result.details?.failedEmails && result.details.failedEmails.length > 0 ? (
                <div className="mt-3">
                  <p className="text-sm font-medium text-red-800">
                    Failed recipients ({result.details.failedEmails.length}) — copy and re-send via the specific list:
                  </p>
                  <Textarea
                    readOnly
                    value={result.details.failedEmails.join('\n')}
                    onFocus={e => e.currentTarget.select()}
                    className="mt-1 h-32 bg-white font-mono text-xs text-gray-800"
                  />
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
